import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ok, useApi } from '../lib/api';
import { useDraft } from '../lib/draft';
import {
  grantLandsIn,
  grantPicks,
  type GrantFilter,
  type GrantItem,
  type GrantSurvival,
} from '../lib/playerSupport';

/**
 * The grant form's item quick-pick (T49, ADR 0055): a filter (every item, or armor only, which lists the dev-only hard-plate tiers
 * too) over the published content (else the local draft), and the line that says where the grant lands: goods always go to the
 * shared pool (`base`), a pack (`carried`, `team:`) takes only raw materials.
 */
export function GrantItemPicker({
  itemId,
  location,
  quantity,
  onPick,
}: {
  itemId: string;
  location: string;
  quantity: number;
  onPick: (id: string) => void;
}) {
  const { client } = useApi();
  const draft = useDraft();
  const [filter, setFilter] = useState<GrantFilter>('all');
  // The same queries (and cache keys) as useTriggerOptions: the published bundle.
  const current = useQuery({
    queryKey: ['content-current'],
    queryFn: async () => ok(await client.GET('/content/current')),
  });
  const published = useQuery({
    queryKey: ['admin-content', current.data?.version],
    queryFn: async () =>
      ok(
        await client.GET('/admin/content/{version}', {
          params: { path: { version: current.data?.version ?? 0 } },
        }),
      ),
    enabled: current.data !== undefined,
  });
  const bundle = (published.data ?? draft?.bundle) as
    { items?: GrantItem[] | null; survival?: GrantSurvival | null } | undefined;
  const items = bundle?.items ?? [];
  const picks = grantPicks(items, filter);
  const item = items.find((i) => i.id === itemId);
  const lands = itemId ? grantLandsIn(location, item, quantity, bundle?.survival) : location;

  const chip = (f: GrantFilter, label: string) => (
    <button
      type="button"
      aria-pressed={filter === f}
      onClick={() => setFilter(f)}
      className={`rounded border-2 border-ink px-2 ${filter === f ? 'bg-nonphoto font-semibold' : 'bg-white'}`}
    >
      {label}
    </button>
  );

  return (
    <div className="col-span-2 space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-bruise">Quick pick</span>
        {chip('all', 'All')}
        {chip('armor', 'Armor')}
        <select
          aria-label="Pick an item"
          value={picks.some((p) => p.id === itemId) ? itemId : ''}
          onChange={(e) => e.target.value && onPick(e.target.value)}
          className="min-w-0 flex-1 rounded border-2 border-ink px-2 py-1"
        >
          <option value="">
            {items.length === 0 ? 'loading items…' : `${picks.length} items`}
          </option>
          {picks.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </div>
      <p className="text-xs text-bruise">
        Goods (weapons, ammo, armor, meds, tools) land in the shared pool (base) wherever you send
        them; a pack (carried or a team) takes only raw materials.
        {lands !== location && (
          <strong className="ml-1 text-ink">
            {itemId} is goods: it lands in base, not {location}.
          </strong>
        )}
      </p>
    </div>
  );
}
